import React, { useState, useEffect } from 'react';
import { Code, Download, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';

const XBRLPackage = ({ onSubmit, onNext, onPrevious, initialData = {} }) => {
  const [enabled, setEnabled] = useState(initialData.enabled || false);
  const [taxonomy, setTaxonomy] = useState(initialData.taxonomy || 'efrag-esrs-latest');
  const [validationResults, setValidationResults] = useState(initialData.validationResults || null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [packageGenerated, setPackageGenerated] = useState(initialData.packageGenerated || false);
  const [packageInfo, setPackageInfo] = useState(initialData.packageInfo || null);

  useEffect(() => {
    // Load initial data when component mounts
  }, [initialData]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'passed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      default:
        return <div className="w-5 h-5 bg-gray-200 rounded-full" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'passed':
        return 'text-green-600';
      case 'failed':
        return 'text-red-600';
      case 'warning':
        return 'text-yellow-600';
      default:
        return 'text-gray-500';
    }
  };

  const generateXBRL = async () => {
    setIsGenerating(true);
    
    // Simulate XBRL generation
    await new Promise(resolve => setTimeout(resolve, 4000));
    
    const packageData = {
      filename: 'esrs_package.zip',
      xbrlFile: 'instance.xbrl',
      taxonomyRefs: 'taxonomy-references.xml',
      size: '1.2 MB',
      generatedAt: new Date().toISOString()
    };
    
    setPackageInfo(packageData);
    setPackageGenerated(true);
    setIsGenerating(false);
  };

  const validateXBRL = async () => {
    setIsValidating(true);
    
    // Simulate validation
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    const results = {
      schema: { status: 'passed', message: 'Schema validation passed' },
      calculations: { status: 'passed', message: 'Calculation validation passed' },
      consistency: { status: 'warning', message: 'Minor consistency warnings found' },
      errors: [],
      warnings: [
        'Element E1-1-1: Missing optional attribute',
        'Element E1-2-1: Value outside expected range'
      ]
    };
    
    setValidationResults(results);
    setIsValidating(false);
  };

  const hasCriticalErrors = () => {
    return validationResults && validationResults.errors.length > 0;
  };


  if (!enabled) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">XBRL Package (Optional)</h2>
          <p className="text-gray-600">Create ESRS XBRL package for regulatory compliance</p>
        </div>

        <div className="text-center py-12">
          <Code className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">XBRL Not Required</h3>
          <p className="text-gray-500 mb-6">
            XBRL package generation is not required for this report. 
            This step will be marked as N/A in the Definition of Done.
          </p>
          <label className="flex items-center justify-center">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm text-gray-700">Enable XBRL package generation</span>
          </label>
        </div>

      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">XBRL Package Generation</h2>
        <p className="text-gray-600">Create ESRS XBRL package with validation</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column - Configuration */}
        <div className="space-y-6">
          {/* Enable/Disable Toggle */}
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="mr-2"
              />
              <span className="text-lg font-semibold text-gray-700">Enable ESRS XBRL package</span>
            </label>
          </div>


          {/* Taxonomy Selection */}
          <div>
            <h3 className="text-lg font-semibold text-gray-700 mb-3">Taxonomy</h3>
            <select
              value={taxonomy}
              onChange={(e) => setTaxonomy(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="efrag-esrs-latest">EFRAG ESRS (Latest Supported)</option>
              <option value="efrag-esrs-2024">EFRAG ESRS 2024</option>
              <option value="efrag-esrs-2023">EFRAG ESRS 2023</option>
              <option value="custom">Custom Taxonomy</option>
            </select>
            <p className="text-sm text-gray-500 mt-1">
              Defaults to latest supported version
            </p>
          </div>

          {/* Validation Types */}
          <div>
            <h3 className="text-lg font-semibold text-gray-700 mb-3">Validation</h3>
            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex items-center">
                <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                Schema validation
              </div>
              <div className="flex items-center">
                <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                Calculation validation
              </div>
              <div className="flex items-center">
                <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                Consistency checks
              </div>
            </div>
          </div>

          {/* Package Contents */}
          <div>
            <h3 className="text-lg font-semibold text-gray-700 mb-3">Package Contents</h3>
            <div className="space-y-2 text-sm text-gray-600">
              <div>• .xbrl instance file</div>
              <div>• Package (.zip) with taxonomy references</div>
              <div>• Validation report</div>
            </div>
          </div>
        </div>

        {/* Right Column - Generation & Validation */}
        <div className="space-y-6">
          {/* Validate Button */}
          {packageGenerated && (
            <div className="text-center">
              <button
                onClick={validateXBRL}
                disabled={isValidating}
                className={`flex items-center justify-center w-full px-6 py-4 rounded-md transition-colors ${
                  isValidating
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-green-500 text-white hover:bg-green-600'
                }`}
              >
                <CheckCircle className="w-5 h-5 mr-2" />
                {isValidating ? 'Validating...' : 'Validate'}
              </button>
            </div>
          )}

          {/* Package Info */}
          {packageGenerated && packageInfo && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="font-semibold text-blue-800 mb-2">Package Generated</h3>
              <div className="space-y-1 text-sm text-blue-700">
                <p><strong>Filename:</strong> {packageInfo.filename}</p>
                <p><strong>XBRL File:</strong> {packageInfo.xbrlFile}</p>
                <p><strong>Size:</strong> {packageInfo.size}</p>
                <p><strong>Generated:</strong> {new Date(packageInfo.generatedAt).toLocaleString()}</p>
              </div>
              <button className="mt-3 flex items-center text-blue-600 hover:text-blue-700">
                <Download className="w-4 h-4 mr-1" />
                Download Package
              </button>
            </div>
          )}

          {/* Validation Results */}
          {validationResults && (
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <h3 className="font-semibold text-gray-800 mb-3">Validation Results</h3>
              
              {/* Validation Checks */}
              <div className="space-y-2 mb-4">
                {Object.entries(validationResults).filter(([key]) => ['schema', 'calculations', 'consistency'].includes(key)).map(([key, result]) => (
                  <div key={key} className="flex items-center">
                    {getStatusIcon(result.status)}
                    <span className={`ml-2 text-sm ${getStatusColor(result.status)}`}>
                      {result.message}
                    </span>
                  </div>
                ))}
              </div>

              {/* Errors */}
              {validationResults.errors.length > 0 && (
                <div className="mb-3">
                  <h4 className="font-medium text-red-700 mb-2">Errors ({validationResults.errors.length})</h4>
                  <div className="space-y-1">
                    {validationResults.errors.map((error, index) => (
                      <div key={index} className="text-sm text-red-600 bg-red-50 p-2 rounded">
                        {error}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Warnings */}
              {validationResults.warnings.length > 0 && (
                <div>
                  <h4 className="font-medium text-yellow-700 mb-2">Warnings ({validationResults.warnings.length})</h4>
                  <div className="space-y-1">
                    {validationResults.warnings.map((warning, index) => (
                      <div key={index} className="text-sm text-yellow-600 bg-yellow-50 p-2 rounded">
                        {warning}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Generate Button - Bottom Right */}
      <div className="flex justify-end mt-6">
        <button
          onClick={generateXBRL}
          disabled={isGenerating}
          className={`px-6 py-3 rounded-md transition-colors ${
            isGenerating
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-blue-500 text-white hover:bg-blue-600'
          }`}
        >
          {isGenerating ? 'Generating XBRL...' : 'Generate XBRL'}
        </button>
      </div>

      {hasCriticalErrors() && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700 text-sm">
            Critical validation errors detected. Please resolve all errors before proceeding.
          </p>
        </div>
      )}
    </div>
  );
};

export default XBRLPackage;
